/* Panier maison MLP : le composant <mlp-cart> (tiroir + page /cart), JavaScript sans dépendance.
   Besoin de : assets/mlp-cart-engine.js (moteur des paliers), et pour les US assets/mlp-protection.js (protection colis).
   Principes (guide panier-tiroir-maison.md) :
   1. Tous les montants viennent du Liquid : après chaque écriture on relit la section (?sections=mlp-cart).
   2. /cart.js et la section partent en parallèle ; le plan des cadeaux part dès /cart.js.
   3. Écritures : cart/change.js par clé de ligne, cart/update.js pour cadeaux, options, codes et protection.
      Jamais cart/clear.js, jamais un objet attributes complet (seulement nos clés).
   4. Toute écriture panier, même faite par une app, rafraîchit le tiroir (fetch et XHR enveloppés).
   5. Une écriture à la fois (file). Le tiroir s'ouvre AVANT la réponse d'un ajout, avec une ligne fantôme.
   6. Mémoire (choix et refus de cadeaux) liée au jeton du panier, localStorage, cookie de repli. */
(() => {
  if (window.customElements.get('mlp-cart')) return;
  const E = window.MLPCartEngine;
  const CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  const ADD_RE = /\/cart\/add(\.js)?(\?|$)/i;
  const OWN = 'X-MLP-Cart';
  const MEM_KEY = 'mlp_cart_mem';
  const PREVIEW_KEY = 'mlp_cart_preview';
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const publish = (name, data) => { try { window.Shopify.analytics.publish('mlp_cart_' + name, data || {}); } catch (e) { /* pas de pixel : évènement perdu */ } };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Interrupteur : on = tout le monde ; preview = seulement avec ?mlp_cart=1 (mémorisé, ?mlp_cart=0 pour sortir).
  function previewFlag() {
    try {
      const q = new URLSearchParams(location.search).get('mlp_cart');
      if (q === '1') localStorage.setItem(PREVIEW_KEY, '1');
      if (q === '0') localStorage.removeItem(PREVIEW_KEY);
      return localStorage.getItem(PREVIEW_KEY) === '1';
    } catch (e) {
      return new URLSearchParams(location.search).get('mlp_cart') === '1';
    }
  }

  // Mémoire liée au panier : localStorage, cookie si le stockage est bloqué.
  const store = {
    read() {
      try { return JSON.parse(localStorage.getItem(MEM_KEY) || 'null'); } catch (e) {
        const m = document.cookie.match(new RegExp('(?:^|; )' + MEM_KEY + '=([^;]*)'));
        try { return m ? JSON.parse(decodeURIComponent(m[1])) : null; } catch (e2) { return null; }
      }
    },
    write(v) {
      const s = JSON.stringify(v);
      try { localStorage.setItem(MEM_KEY, s); } catch (e) {
        document.cookie = MEM_KEY + '=' + encodeURIComponent(s) + ';path=/;max-age=1209600;SameSite=Lax';
      }
    },
  };

  class MLPCart extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const sw = this.dataset.switch;
      if (!(sw === 'on' || (sw === 'preview' && previewFlag()))) return; // éteint : le tiroir du thème reste en place
      if (!E) return console.warn('[mlp-cart] moteur absent (mlp-cart-engine.js)');
      if (window.cornerRenderConfig) console.warn('[mlp-cart] Corner est encore actif : couper son app embed avant la mise en ligne');
      this.active = true;
      this.page = this.dataset.mode === 'page';
      this._queue = Promise.resolve();
      this._sessionReminder = false;
      document.documentElement.classList.add('mlp-cart-on');
      this.readConfig();
      this.bind();
      this.hookNetwork();
      this.hookTheme();
      if (this.page) this.mountPage();
      this.removeAttribute('hidden');
      this.refresh();
    }

    readConfig() {
      const node = this.querySelector('[data-mlp-cart-config]');
      try { this.cfg = JSON.parse(node.textContent); } catch (e) { this.cfg = { tiers: [] }; }
      this.cfg.rate = Number(window.Shopify && Shopify.currency && Shopify.currency.rate) || 0;
      this.money = E.moneyFormatter(this.cfg.moneySample);
    }

    get mem() {
      const m = store.read();
      return m && m.token === (this.cart && this.cart.token) ? m : { token: this.cart && this.cart.token, choice: {} };
    }
    set mem(v) { store.write(Object.assign({}, v, { token: this.cart && this.cart.token })); }

    // ---------- Réseau : une écriture à la fois ----------
    enqueue(fn) {
      const run = this._queue.then(fn, fn);
      this._queue = run.catch((e) => { console.error('[mlp-cart]', e); this.flash(this.cfg.copy.error); });
      return run;
    }
    async req(path, body) {
      // window.fetch lu à l'appel : les apps de test de prix l'enveloppent après nous.
      const r = await window.fetch(root() + path, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: Object.assign({ Accept: 'application/json', [OWN]: '1' }, body ? { 'Content-Type': 'application/json' } : {}),
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw Object.assign(new Error(data.description || data.message || r.status), { data, status: r.status });
      return data;
    }
    getCart() { return this.req('cart.js'); }
    async getSection() {
      const url = location.pathname + '?sections=mlp-cart';
      const r = await window.fetch(url, { credentials: 'same-origin', headers: { [OWN]: '1' } });
      const json = await r.json();
      return json['mlp-cart'];
    }

    // Lecture + mise d'aplomb (cadeaux, protection) + rendu. Un seul rendu après ajout + cadeau.
    refresh() {
      // Un rafraîchissement en attente sert à tous les appels qui arrivent avant son départ (ajout + évènement du thème).
      if (this._refreshQueued) return this._refreshQueued;
      const run = this.enqueue(async () => {
        this._refreshQueued = null;
        const sectionP = this.getSection().catch(() => null);
        let cart = await this.getCart();
        this.cart = cart;
        let dirty = false;
        const p = E.plan(cart, this.cfg, this.mem);
        if (p.blocked.length) {
          // Corner n'offre pas ce cadeau à ce montant (écart d'arrondi) : on ne le remet pas tant que le panier ne bouge pas
          const m = this.mem;
          m.blocked = Object.assign({}, m.blocked);
          p.blocked.forEach((code) => { m.blocked[code] = p.state.base; });
          this.mem = m;
        }
        if (p.changed) {
          if (Object.keys(p.updates).length) cart = await this.req('cart/update.js', { updates: p.updates });
          if (p.adds.length) {
            await this.req('cart/add.js', { items: p.adds }).catch(() => null);
            cart = await this.getCart();
          }
          dirty = true;
        }
        if (window.MLPProtection && cart.items.some(window.MLPProtection.isProtection)) {
          const fixed = await window.MLPProtection.sync(cart);
          if (fixed !== cart) { cart = fixed; dirty = true; }
        }
        this.cart = cart;
        const html = dirty ? await this.getSection() : await sectionP;
        this.render(html, cart);
      });
      this._refreshQueued = run;
      return run;
    }

    // ---------- Rendu ----------
    render(html, cart) {
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.querySelector('[data-mlp-body]');
        const conf = doc.querySelector('[data-mlp-cart-config]');
        const host = this.querySelector('[data-mlp-body]');
        if (body) host.replaceChildren(...body.childNodes);
        host.classList.remove('is-loading');
        if (conf) { this.querySelector('[data-mlp-cart-config]').textContent = conf.textContent; this.readConfig(); }
      }
      const st = E.state(cart, this.cfg, this.mem);
      this.st = st;
      document.dispatchEvent(new CustomEvent('mlp:cart-state', { detail: { st } }));
      this.renderGauge(st);
      this.renderGifts(st);
      this.prepareCards(cart);
      this.renderGap(st, cart);
      this.renderCode();
      this.renderSlider();
      const recos = this.querySelector('[data-mlp-recos]');
      if (recos) recos.classList.toggle('is-done', st.allReached);
      const express = this.querySelector('[data-mlp-express]');
      if (express) express.hidden = !!(this.cfg.expressHidePending && st.pending.length);
      this.updateCount(st.paid + cart.items.filter((it) => E.isGiftLine(it, this.cfg)).reduce((n, it) => n + it.quantity, 0));
      try { window.Shopify && Shopify.PaymentButton && Shopify.PaymentButton.init(); } catch (e) { /* boutons express absents */ }
      this.celebrate(st);
    }

    // Jauge du panier Naked : une étape par palier (colonnes égales), photo du cadeau dans un rond,
    // coche noire une fois atteint, « Nom · seuil » dessous, barre noire remplie d'étape en étape.
    renderGauge(st) {
      const box = this.querySelector('[data-mlp-gauge]');
      if (!box) return;
      const c = this.cfg.copy;
      const tiers = st.tiers;
      const ship = st.shipping;
      if (!tiers.length && !ship) { box.hidden = true; return; }
      box.hidden = false;
      const fmt = (cents) => `<strong>${esc(this.money(cents))}</strong>`;
      // Mystery gift (Lisa 19/10) : on ne dit pas quel cadeau avant le palier, comme Corner (« Gift 1 », « Free Gift 🎁 »)
      // Palier « révélé » (Lisa 21/10, legging offert) : nom court + photo dans la jauge, nom et valeur dans le message.
      const name = (t) => (t.reveal && t.label) || (c.gift_n || 'Gift [N]').replace('[N]', tiers.indexOf(t) + 1);
      const value = (t) => (t.options[0] && t.options[0].value) || '';
      const fill = (tpl, t, amount) => {
        const v = value(t);
        return tpl.replace(v ? '[VALUE]' : ' ([VALUE])', v ? `<strong>${esc(v)}</strong>` : '').replace('[GIFT]', esc(name(t))).replace('[AMOUNT]', amount);
      };
      const reveal = (t, amount) => this.revealText(c.gauge_reveal, t).replace('[AMOUNT]', amount);
      // Un seul message à la fois. Le prochain palier débloque aussi la livraison : on le dit dans la même phrase.
      let msg;
      const next = st.next;
      if (!st.paid && next) {
        msg = fill(c.gauge_from, next, fmt(next.cents));
      } else if (!st.paid && ship) {
        msg = c.gauge_ship_from.replace('[AMOUNT]', fmt(ship.cents));
      } else if (next && next.reveal && c.gauge_reveal) {
        msg = reveal(next, fmt(next.missing));
      } else if (next) {
        const both = ship && !ship.reached && ship.cents <= next.cents;
        msg = fill(both ? c.gauge_gift_ship : c.gauge_gift, next, fmt(next.missing));
      } else if (ship && !ship.reached) {
        msg = c.gauge_ship.replace('[AMOUNT]', fmt(ship.missing));
      } else {
        msg = esc(tiers.length ? c.gauge_done : c.gauge_ship_done);
      }
      // Étapes : les paliers ; sans palier, la seule livraison offerte.
      const steps = tiers.length
        ? tiers.map((t) => ({ cents: t.cents, reached: t.reached, label: name(t), image: (t.reveal && t.options[0] && t.options[0].image) || '' }))
        : [{ cents: ship.cents, reached: ship.reached, label: c.step_ship, image: '' }];
      const n = steps.length;
      const done = steps.filter((s) => s.reached).length;
      const nextStep = steps[done];
      const prev = done ? steps[done - 1].cents : 0;
      const part = nextStep ? Math.max(0, Math.min(1, (st.base - prev) / Math.max(1, nextStep.cents - prev))) : 0;
      const pct = Math.min(100, ((done + part) / n) * 100);
      const ok = '<span class="mlp-goal__ok"><svg aria-hidden="true" focusable="false" width="10" height="8" viewBox="0 0 10 8"><path d="M1 4l2.7 2.7L9 1.3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
      const items = steps.map((s, i) => {
        const cls = (s.reached ? ' is-reached' : i === done ? ' is-next' : '') + (s.image ? ' is-reveal' : '');
        const truck = '<svg aria-hidden="true" focusable="false" width="16" height="13" viewBox="0 0 16 13"><path d="M1 1.5h9v7.5H1zM10 4.5h3l2 2.5v2H10" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><circle cx="4" cy="10.5" r="1.4" fill="#fff" stroke="currentColor" stroke-width="1.2"/><circle cx="12" cy="10.5" r="1.4" fill="#fff" stroke="currentColor" stroke-width="1.2"/></svg>';
        const giftIco = '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16"><path d="M2 6.5h12v3H2zM3 9.5h10V15H3zM8 6.5V15M8 6.5C6.5 6.5 4.5 6 4.5 4.3 4.5 3.2 5.3 2.6 6.1 2.8 7.3 3.1 8 6.5 8 6.5zm0 0c1.5 0 3.5-.5 3.5-2.2 0-1.1-.8-1.7-1.6-1.5C8.7 3.1 8 6.5 8 6.5z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>';
        const pic = s.image ? `<img src="${esc(s.image)}" alt="" width="40" height="40" loading="lazy">` : tiers.length ? giftIco : truck;
        const label = s.reached
          ? `<span class="mlp-goal__name">${esc(c.free)}</span>`
          : `<span class="mlp-goal__name">${esc(s.label)}</span><span class="mlp-goal__rest"><span class="mlp-num">${esc(this.money(s.cents))}</span></span>`;
        return `<li class="mlp-goal__step${cls}"><span class="mlp-goal__dot"><span class="mlp-goal__pic">${pic}</span>${s.reached ? ok : ''}</span>
          <span class="mlp-goal__label" title="${esc(s.label)} · ${esc(this.money(s.cents))}">${label}</span></li>`;
      }).join('');
      box.innerHTML = `<p class="mlp-goal__text">${msg}</p>
        <div class="mlp-goal__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}" style="--mlp-fill:${pct.toFixed(2)}%;--mlp-steps:${n}">
          <span class="mlp-goal__fill"></span><ol class="mlp-goal__steps" role="list">${items}</ol></div>`;
    }

    // Paliers atteints sans cadeau choisi : choix en un clic, dans la bande beige des cadeaux. Refusé : lien pour le reprendre.
    renderGifts(st) {
      const box = this.querySelector('[data-mlp-gifts]');
      if (!box) return;
      const c = this.cfg.copy;
      const rows = st.tiers.filter((t) => t.reached && !t.chosen);
      box.innerHTML = rows.map((t) => {
        if (t.refused) return `<button type="button" class="mlp-link mlp-gifts__retake" data-mlp-gift-retake="${esc(t.id)}">${esc(c.gift_retake)}</button>`;
        return `<div class="mlp-gifts__tier"><p class="mlp-gifts__title">${rows.length > 1 ? esc((c.gift_n || 'Gift [N]').replace('[N]', st.tiers.indexOf(t) + 1)) + ' · ' : ''}${esc(c.gift_pick)}</p>
          <div class="mlp-gifts__options">${this.giftOptions(t)}</div></div>`;
      }).join('');
      box.hidden = !rows.length;
      const zone = this.querySelector('[data-mlp-giftzone]');
      if (zone && rows.length) zone.hidden = false;
      // « Offert · dès 120 $ » sur chaque cadeau au panier : le seuil du palier qui le donne.
      this.querySelectorAll('[data-mlp-gift-from]').forEach((el) => {
        const vid = Number(el.dataset.vid);
        const t = st.tiers.find((x) => x.code && x.code === el.dataset.code) || st.tiers.find((x) => Number(x.chosen) === vid);
        el.textContent = t ? ' · ' + c.gift_from.replace('[AMOUNT]', this.money(t.cents)) : '';
      });
      // « Change my gift » : seulement si le palier propose un autre cadeau dispo.
      this.querySelectorAll('[data-mlp-gift-change]').forEach((el) => {
        const vid = Number(el.dataset.mlpGiftChange);
        const t = st.tiers.find((x) => x.code && x.code === el.dataset.code && Number(x.chosen) === vid) || st.tiers.find((x) => Number(x.chosen) === vid);
        el.hidden = !(t && t.options.some((g) => Number(g.vid) !== vid));
      });
    }
    // « Add … to get a FREE Perfect Fit Legging ($95 value) » : nom du cadeau (titre du produit) et valeur en gras.
    revealText(tpl, t) {
      const g = t.options[0] || {};
      return esc(tpl).replace('[GIFT]', `<strong>${esc(g.title || t.label || '')}</strong>`).replace('[VALUE]', `<strong>${esc(g.value || '')}</strong>`);
    }
    giftOptions(t) {
      const c = this.cfg.copy;
      // Cadeau à plusieurs options (legging : couleur + taille) : une seule carte avec ses sélecteurs.
      const groups = {};
      t.options.forEach((g) => { if (g.opts && g.opts.length > 1) (groups[g.pid] = groups[g.pid] || []).push(g); });
      // Seulement si le palier propose plusieurs variantes du produit (les chaussettes : une variante par produit, carte simple)
      Object.keys(groups).forEach((pid) => { if (groups[pid].length < 2) delete groups[pid]; });
      const done = new Set();
      return t.options.map((g) => {
        if (groups[g.pid]) {
          if (done.has(g.pid)) return '';
          done.add(g.pid);
          return this.giftGroup(t, groups[g.pid]);
        }
        return this.giftButton(t, g);
      }).join('');
    }
    giftGroup(t, list) {
      const c = this.cfg.copy;
      const names = list[0].optNames || [];
      const size = E.preferredSize(this.cart || { items: [] });
      const sizeIdx = names.findIndex((n) => /^(taille|size|gr(ö|o)(ß|ss)e)$/i.test(n));
      const first = (sizeIdx >= 0 && size && list.find((g) => g.opts[sizeIdx] === size)) || list[0];
      const selects = names.map((n, i) => {
        const vals = [...new Set(list.map((g) => g.opts[i]))];
        return `<label class="mlp-gift__opt"><span class="mlp-sr">${esc(n)}</span><select class="mlp-select" data-mlp-gift-opt="${i}" aria-label="${esc(n)}">${vals.map((v) => `<option value="${esc(v)}"${v === first.opts[i] ? ' selected' : ''}>${esc(v)}</option>`).join('')}</select></label>`;
      }).join('');
      return `<div class="mlp-gift mlp-gift--group" data-mlp-gift-group data-tier="${esc(t.id)}" data-variants="${esc(JSON.stringify(list.map((g) => ({ vid: g.vid, o: g.opts, img: g.image }))))}">
          <span class="mlp-gift__media"><img data-mlp-gift-img src="${esc(first.image || '')}" alt="" loading="lazy"></span>
          <span class="mlp-gift__main">
            <span class="mlp-gift__name">${esc(first.title)}</span>
            <span class="mlp-gift__value"><span class="mlp-tag">${esc(c.free)}</span>${first.value ? ` <s class="mlp-was">${esc(first.value)}</s>` : ''}</span>
            <span class="mlp-gift__opts">${selects}</span>
          </span>
          <button type="button" class="mlp-btn mlp-btn--sm mlp-btn--outline mlp-gift__cta" data-mlp-gift-take="${esc(t.id)}" data-vid="${first.vid}">${esc(c.gift_take)}</button>
        </div>`;
    }
    // Sélecteurs d'une carte cadeau groupée : variante choisie = celle des valeurs sélectionnées, sinon la 1re dispo qui garde la valeur touchée.
    syncGiftGroup(card, changed) {
      let list = [];
      try { list = JSON.parse(card.dataset.variants); } catch (e) { return; }
      const sels = [...card.querySelectorAll('[data-mlp-gift-opt]')];
      const vals = sels.map((s) => s.value);
      let v = list.find((x) => x.o.every((val, i) => val === vals[i]));
      if (!v) {
        const i = Number(changed);
        v = list.find((x) => x.o[i] === vals[i]) || list[0];
        sels.forEach((s, k) => { s.value = v.o[k]; });
      }
      const btn = card.querySelector('[data-mlp-gift-take]');
      if (btn) btn.dataset.vid = v.vid;
      const img = card.querySelector('[data-mlp-gift-img]');
      if (img && v.img) img.src = v.img;
    }
    giftButton(t, g) {
      const c = this.cfg.copy;
      return `<button type="button" class="mlp-gift" data-mlp-gift-take="${esc(t.id)}" data-vid="${g.vid}">
          <span class="mlp-gift__media">${g.image ? `<img src="${esc(g.image)}" alt="" loading="lazy">` : ''}</span>
          <span class="mlp-gift__main">
            <span class="mlp-gift__name">${esc(g.title)}${g.variant ? ' · ' + esc(g.variant) : ''}</span>
            <span class="mlp-gift__value"><span class="mlp-tag">${esc(c.free)}</span>${g.value ? ` <s class="mlp-was">${esc(g.value)}</s>` : ''}</span>
          </span>
          <span class="mlp-btn mlp-btn--sm mlp-btn--outline mlp-gift__cta">${esc(c.gift_take)}</span>
        </button>`;
    }

    // Cartes : taille la plus présente au panier, variante suivie à chaque changement de sélecteur.
    prepareCards(cart) {
      const size = E.preferredSize(cart);
      this.querySelectorAll('[data-mlp-card]').forEach((card) => {
        if (card._mlp) return;
        card._mlp = true;
        let variants = [];
        try { variants = JSON.parse(card.querySelector('[data-mlp-card-variants]').textContent); } catch (e) { /* carte sans variantes */ }
        card._variants = variants;
        const selects = [...card.querySelectorAll('[data-mlp-card-option]')];
        const isSize = (sel) => /^(taille|size|gr(ö|o)(ß|ss)e)$/i.test(sel.dataset.name);
        let sized = !selects.some(isSize);
        if (size) selects.forEach((sel) => {
          if (isSize(sel) && [...sel.options].some((o) => o.value === size)) { sel.value = size; sized = true; }
        });
        // « + » direct : pièce sans taille, ou taille de la cliente connue. Sinon il déplie les sélecteurs.
        card._sized = card.dataset.single === 'true' || sized;
        this.syncCard(card);
      });
    }
    syncCard(card) {
      const values = [...card.querySelectorAll('[data-mlp-card-option]')].map((s) => s.value);
      const v = card._variants.find((x) => values.every((val, i) => x.o[i] === val)) || card._variants[0];
      const btn = card.querySelector('[data-mlp-card-add]');
      if (!v || !btn) return;
      btn.dataset.vid = v.id;
      btn.disabled = !v.a;
      const price = card.querySelector('[data-mlp-card-price]');
      if (price && v.p) price.textContent = v.p;
      // Carte « combler l'écart » et popup : le bouton dit le prix (« Ajouter · 60 € »)
      const txt = btn.querySelector('.mlp-rc__addtxt');
      if (txt) txt.textContent = card.matches('.mlp-rc--gap') && v.p ? btn.dataset.label + ' · ' + v.p : btn.dataset.label;
    }

    // Carte « combler l'écart » (en haut, sous la jauge) : la pièce qui fait passer le palier suivant, dans sa taille.
    renderGap(st, cart) {
      const box = this.querySelector('[data-mlp-gap]');
      if (!box) return;
      box.replaceChildren();
      // Rendu sans relecture de la section : la carte cachée au tour précédent revient dans le carrousel.
      this.querySelectorAll('[data-mlp-recos] [data-mlp-card][hidden]').forEach((el) => { el.hidden = false; });
      const title = this.querySelector('[data-mlp-reco-title]');
      if (title) title.textContent = title.dataset.title;
      const target = st.next || (st.shipping && !st.shipping.reached ? Object.assign({ id: null, options: [] }, st.shipping) : null);
      if (!target || !st.paid) return;
      const cards = [...this.querySelectorAll('[data-mlp-recos] [data-mlp-card]')].map((el) => ({ el, pid: Number(el.dataset.pid), price: Number(el.dataset.price), available: el.dataset.available === 'true' }));
      const pick = E.gapPick(cards, target.missing, cart.items.map((it) => it.product_id));
      if (!pick) return;
      const c = this.cfg.copy;
      const clone = pick.el.cloneNode(true);
      clone._mlp = false;
      clone.hidden = false;
      clone.classList.add('mlp-rc--gap');
      const t = clone.querySelector('[data-mlp-card-title]');
      if (t) t.textContent = c.gap_title.replace('[NAME]', pick.el.dataset.name || t.textContent).replace('[AMOUNT]', this.money(target.missing));
      const gap = clone.querySelector('[data-mlp-card-gap]');
      if (gap) {
        if (target.id && target.reveal && c.gap_reveal) {
          gap.innerHTML = this.revealText(c.gap_reveal, target);
        } else if (target.id) {
          gap.textContent = c.gap_gift;
        } else {
          gap.textContent = c.gap_ship;
        }
        gap.hidden = false;
      }
      pick.el.hidden = true;
      box.appendChild(clone);
      if (title) title.textContent = title.dataset.titleGap;
      this.prepareCards(cart);
    }

    // Code promo replié : il reste ouvert d'un rendu à l'autre une fois déplié.
    renderCode() {
      const form = this.querySelector('[data-mlp-code-form]');
      const btn = this.querySelector('[data-mlp-code-toggle]');
      if (!form || !btn) return;
      form.hidden = !this._codeOpen;
      btn.setAttribute('aria-expanded', String(!!this._codeOpen));
    }
    // Recommandations en carrousel : flèches actives selon la position.
    renderSlider() {
      const track = this.querySelector('[data-mlp-reco-track]');
      if (!track) return;
      const arrows = this.querySelectorAll('[data-mlp-slide]');
      const sync = () => arrows.forEach((a) => {
        const back = a.dataset.mlpSlide === '-1';
        const off = back ? track.scrollLeft <= 2 : track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        a.setAttribute('aria-disabled', String(off));
      });
      track.addEventListener('scroll', sync, { passive: true });
      sync();
    }

    // Palier tout juste atteint : petits confettis, une fois.
    celebrate(st) {
      const n = st.tiers.filter((t) => t.reached).length;
      const m = this.mem;
      if (m.reached != null && n > m.reached) {
        const box = this.querySelector('[data-mlp-gauge]');
        if (box && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          const burst = document.createElement('div');
          burst.className = 'mlp-confetti';
          burst.innerHTML = '<i></i>'.repeat(14);
          box.appendChild(burst);
          setTimeout(() => burst.remove(), 1400);
        }
        publish('tier_reached', { tier: n });
      }
      if (m.reached !== n) this.mem = Object.assign(m, { reached: n });
    }

    updateCount(n) {
      document.querySelectorAll('cart-count').forEach((el) => {
        try { el.itemCount = n; } catch (e) { el.textContent = n; }
      });
      const own = this.querySelector('[data-mlp-count]');
      if (own) own.textContent = '(' + n + ')';
    }

    flash(text) {
      const el = this.querySelector('[data-mlp-code-error]');
      if (el && text) {
        el.textContent = text;
        el.hidden = false;
        this._codeOpen = true;
        this.renderCode();
      }
    }

    // ---------- Ouverture ----------
    open() {
      if (this.page || this.isOpen) return;
      this.isOpen = true;
      // Aperçu de thème : la barre Shopify « Draft » couvre le bas de l'écran ; le pied du tiroir passe au-dessus.
      const pbar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
      this.style.setProperty('--mlp-pbar', pbar ? Math.round(pbar.getBoundingClientRect().height) + 'px' : '0px');
      this.classList.add('is-open');
      document.documentElement.classList.add('mlp-cart-lock');
      try { history.pushState({ mlpc: 1 }, ''); } catch (e) { /* historique indisponible */ }
      const panel = this.querySelector('.mlp-cart__panel');
      if (panel) panel.focus({ preventScroll: true });
      publish('open');
    }
    close(fromHistory) {
      if (this.page || !this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('mlp-cart-lock');
      if (!fromHistory && history.state && history.state.mlpc) history.back();
    }
    // Ajout en cours (bouton du thème ou d'une app) : le tiroir s'ouvre tout de suite, ligne fantôme.
    ghost() {
      if (this.page || !this.cfg.openOnAdd) return;
      this.open();
      const lines = this.querySelector('[data-mlp-lines]');
      if (lines && !lines.querySelector('.mlp-line--ghost')) {
        const li = document.createElement('li');
        li.className = 'mlp-line mlp-line--ghost';
        li.innerHTML = '<div class="mlp-line__media"></div><div class="mlp-line__main"><span></span><span></span></div>';
        lines.prepend(li);
      } else if (!lines) {
        this.querySelector('[data-mlp-body]').classList.add('is-loading');
      }
    }
    mountPage() {
      const slot = document.getElementById('mlp-cart-page');
      if (slot) slot.appendChild(this);
      this.classList.add('mlp-cart--page', 'is-open');
    }

    // ---------- Actions ----------
    bind() {
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-mlp-close],[data-mlp-qty],[data-mlp-refuse],[data-mlp-gift-change],[data-mlp-gift-take],[data-mlp-gift-retake],[data-mlp-card-add],[data-mlp-code-remove],[data-mlp-go],[data-mlp-reminder-skip],[data-mlp-pop-close],[data-mlp-pop-yes],[data-mlp-pop-no],[data-mlp-pop-pay],[data-mlp-code-toggle],[data-mlp-slide]');
        if (!t) return;
        if (t.matches('[data-mlp-close]')) return this.close();
        e.preventDefault();
        if (t.matches('[data-mlp-code-toggle]')) {
          this._codeOpen = !this._codeOpen;
          this.renderCode();
          const input = this._codeOpen && this.querySelector('[data-mlp-code-form] input');
          if (input) input.focus();
          return;
        }
        if (t.matches('[data-mlp-slide]')) {
          const track = this.querySelector('[data-mlp-reco-track]');
          if (track) track.scrollBy({ left: Number(t.dataset.mlpSlide) * track.clientWidth * 0.8, behavior: 'smooth' });
          return;
        }
        if (t.matches('[data-mlp-qty]')) return this.changeQty(t.closest('[data-mlp-line]').dataset.key, Number(t.dataset.mlpQty));
        if (t.matches('[data-mlp-refuse]')) return this.refuseGift(Number(t.dataset.mlpRefuse), t.dataset.code);
        if (t.matches('[data-mlp-gift-change]')) return this.changeGift(Number(t.dataset.mlpGiftChange), t.dataset.code);
        if (t.matches('[data-mlp-gift-take]')) {
          const fromReminder = !!t.closest('[data-mlp-reminder]');
          const done = this.takeGift(t.dataset.mlpGiftTake, Number(t.dataset.vid));
          return fromReminder ? done.then(() => this.checkout(this._wantProtection, true, true)) : done;
        }
        if (t.matches('[data-mlp-gift-retake]')) return this.retakeGift(t.dataset.mlpGiftRetake);
        if (t.matches('[data-mlp-card-add]')) {
          // « + » d'une carte du carrousel sans taille connue : on déplie d'abord les sélecteurs.
          const card = t.closest('[data-mlp-card]');
          if (card && !card._sized && !card.matches('.mlp-rc--gap') && !card.classList.contains('is-picking') && !t.closest('[data-mlp-pop]')) {
            card.classList.add('is-picking');
            const sel = card.querySelector('[data-mlp-card-option]');
            if (sel) sel.focus();
            return;
          }
          return this.addVariant(Number(t.dataset.vid), t);
        }
        if (t.matches('[data-mlp-code-remove]')) return this.removeCode(t.dataset.mlpCodeRemove);
        if (t.matches('[data-mlp-reminder-skip]')) { this._sessionReminder = true; return this.checkout(this._wantProtection, true, true); }
        if (t.matches('[data-mlp-pop-close],[data-mlp-pop-pay]')) return this.popGo();
        if (t.matches('[data-mlp-pop-no]')) return this._pop && this._pop.screen === 'pack' ? this.popGo() : this.showPopup('refused');
        if (t.matches('[data-mlp-pop-yes]')) return this.popYes();
        if (t.matches('[data-mlp-go]')) return this.checkout(t.dataset.mlpGo === '1');
      });
      this.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-mlp-card-option]');
        if (sel) return this.syncCard(sel.closest('[data-mlp-card]'));
        const gsel = e.target.closest('[data-mlp-gift-opt]');
        if (gsel) return this.syncGiftGroup(gsel.closest('[data-mlp-gift-group]'), gsel.dataset.mlpGiftOpt);
        const opt = e.target.closest('[data-mlp-option]');
        if (opt && opt.closest('[data-mlp-pop]')) return; // écran Emballage : pris au départ (un rendu fermerait la popup)
        if (opt) this.toggleOption(Number(opt.dataset.mlpOption), opt.checked);
      });
      this.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-mlp-code-form]');
        if (!form) return;
        e.preventDefault();
        const code = String(new FormData(form).get('code') || '').trim();
        if (code) this.applyCode(code);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const pop = this.querySelector('[data-mlp-pop]');
        if (pop && !pop.hidden) return this.popGo(); // Échap part au paiement, comme la croix
        if (this.isOpen) this.close();
      });
      window.addEventListener('popstate', () => { if (this.isOpen) this.close(true); });
      // Retour par le cache du navigateur (bouton précédent depuis le paiement) : panier relu.
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
    }

    // − / + : le chiffre change tout de suite, les appuis rapides s'additionnent, une seule écriture 350 ms après le dernier.
    changeQty(key, qty) {
      this.busy(true);
      return this.enqueue(() => this.req('cart/change.js', { id: key, quantity: Math.max(0, qty) }))
        .then(() => this.refresh()).finally(() => this.busy(false));
    }
    refuseGift(vid, code) {
      this.mem = E.refuse(this.cart, this.cfg, this.mem, vid, code);
      publish('gift_refuse', { variant: vid });
      return this.refresh();
    }
    changeGift(vid, code) {
      this.mem = E.change(this.cart, this.cfg, this.mem, vid, code);
      publish('gift_change', { variant: vid });
      return this.refresh();
    }
    takeGift(tierId, vid) {
      this.mem = E.choose(this.mem, tierId, vid);
      publish('gift_choose', { tier: tierId, variant: vid });
      return this.refresh();
    }
    retakeGift(tierId) {
      const m = this.mem;
      delete m.choice[tierId];
      this.mem = m;
      return this.refresh();
    }
    addVariant(vid, btn) {
      if (!vid) return;
      if (btn) btn.disabled = true;
      publish('reco_add', { variant: vid, gap: !!(btn && btn.closest('.mlp-rc--gap')) });
      return this.enqueue(() => this.req('cart/add.js', { items: [{ id: vid, quantity: 1 }] }))
        .then(() => this.refresh())
        .finally(() => { if (btn) btn.disabled = false; });
    }
    toggleOption(vid, on) {
      return this.enqueue(() => this.req('cart/update.js', { updates: { [vid]: on ? 1 : 0 } })).then(() => this.refresh());
    }
    async applyCode(code) {
      const current = (this.cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
      if (current.some((c) => c.toLowerCase() === code.toLowerCase())) return;
      const cart = await this.enqueue(() => this.req('cart/update.js', { discount: current.concat(code).join(',') }));
      const hit = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      publish('code_apply', { code, applicable: !!(hit && hit.applicable) });
      if (!hit || !hit.applicable) {
        await this.enqueue(() => this.req('cart/update.js', { discount: current.join(',') }));
        await this.refresh();
        this.flash(this.cfg.copy.code_invalid.replace('[CODE]', code));
        return;
      }
      return this.refresh();
    }
    removeCode(code) {
      const others = (this.cart.discount_codes || []).map((d) => d.code).filter((c) => c.toLowerCase() !== code.toLowerCase());
      // Un lien /discount/CODE pose un cookie que Shopify réapplique au paiement : on l'efface aussi.
      document.cookie = 'discount_code=;path=/;max-age=0';
      return this.enqueue(() => this.req('cart/update.js', { discount: others.join(',') })).then(() => this.refresh());
    }

    // Départ au paiement : UN seul cart/update.js (protection, attributs), puis /checkout. On ne bloque jamais une vente.
    async checkout(wantProtection, skipReminder, skipPopup) {
      this._wantProtection = wantProtection;
      const st = this.st;
      if (!skipReminder && this.cfg.reminder && st && st.pending.length && !this._sessionReminder && !sessionFlag('mlp_cart_reminded')) {
        sessionFlag('mlp_cart_reminded', true);
        return this.showReminder(st);
      }
      if (!skipPopup && this.popupEligible()) return this.showPopup(null);
      this.busy(true);
      const go = () => { window.location.href = root() + 'checkout'; };
      try {
        await this._queue;
        const body = { attributes: { _mlp_cart: '1' } };
        if (this.cfg.popup && this.cfg.popup !== 'off') body.attributes._mlp_pop = this.cfg.popup === 'ab' ? this.popGroup() : 'on';
        if (this._packUpdates) body.updates = Object.assign({}, this._packUpdates);
        const P = window.MLPProtection;
        if (this.cfg.protection && P) {
          const vid = wantProtection ? P.tierVariant(this.cart) : 0;
          body.updates = P.config.variantIds.reduce((u, id) => { u[id] = Number(id) === Number(vid) ? 1 : 0; return u; }, body.updates || {});
          body.attributes._protection_colis = vid ? 'oui' : 'non';
        } else {
          // Diagnostic : pourquoi la protection n'a pas été proposée (hors US, ou script protection pas chargé)
          body.attributes._protection_colis = this.cfg.protection ? 'script-absent' : 'hors-us';
        }
        publish('checkout', { protected: !!(this.cfg.protection && wantProtection), total: this.cart && this.cart.total_price });
        await Promise.race([this.req('cart/update.js', body), sleep(body.updates ? 12000 : 2500)]);
      } catch (e) {
        // panne réseau : on part quand même
      }
      go();
    }
    showReminder(st) {
      const box = this.querySelector('[data-mlp-reminder]');
      const gifts = this.querySelector('[data-mlp-reminder-gifts]');
      if (!box || !gifts) return this.checkout(this._wantProtection, true);
      gifts.innerHTML = st.pending.map((t) => this.giftOptions(t)).join('');
      box.hidden = false;
      publish('reminder_show', { pending: st.pending.length });
    }
    // ---------- Popup « One last thing » : une fois par session, une offre par écran, A/B ----------
    popGroup() {
      let g = null;
      try { g = localStorage.getItem('mlp_pop_ab'); } catch (e) { /* stockage bloqué */ }
      const out = E.abGroup(g, Math.random());
      try { localStorage.setItem('mlp_pop_ab', out); } catch (e) { /* stockage bloqué */ }
      return out;
    }
    popupEligible() {
      const mode = this.cfg.popup || 'off';
      if (mode === 'off' || sessionFlag('mlp_cart_pop')) return false;
      return mode === 'on' || this.popGroup() === 'B';
    }
    showPopup(after) {
      const pop = this.querySelector('[data-mlp-pop]');
      if (!pop || !this.st) return this.checkout(this._wantProtection, true, true);
      const cards = [...this.querySelectorAll('[data-mlp-recos] [data-mlp-card]')].map((el) => ({ el, pid: Number(el.dataset.pid), price: Number(el.dataset.price), available: el.dataset.available === 'true' }));
      const packs = [...this.querySelectorAll('.mlp-options [data-mlp-option][data-in-popup]:not(:checked):not(:disabled)')];
      const r = E.popupScreen(this.st, cards, this.cart.items.map((it) => it.product_id), { reachPct: this.cfg.popupReach || 0.5, packAvailable: packs.length > 0, after });
      if (!r) { pop.hidden = true; return this.checkout(this._wantProtection, true, true); }
      sessionFlag('mlp_cart_pop', true);
      this._pop = r;
      const c = this.cfg.copy;
      const slot = pop.querySelector('[data-mlp-pop-slot]');
      const text = pop.querySelector('[data-mlp-pop-text]');
      const pack = pop.querySelector('[data-mlp-pop-pack]');
      slot.replaceChildren();
      const isPack = r.screen === 'pack';
      text.hidden = isPack;
      pack.hidden = !isPack;
      pop.querySelector('[data-mlp-pop-yes]').hidden = isPack;
      pop.querySelector('[data-mlp-pop-pay]').hidden = !isPack;
      if (isPack) {
        packs.forEach((input) => slot.appendChild(input.closest('.mlp-option').cloneNode(true)));
      } else {
        const t = r.target;
        // Cible = palier cadeau, ou seulement la livraison offerte (phrase à part : accords FR / DE)
        if (t.id && t.reveal && c.pop_reveal) text.innerHTML = this.revealText(c.pop_reveal, t);
        else text.textContent = t.id
          ? (r.screen === 'pieces' ? c.pop_pieces : c.pop_tier)
          : r.screen === 'pieces' ? c.pop_pieces_ship : c.pop_tier_ship;
        const clone = r.card.el.cloneNode(true);
        clone.hidden = false;
        clone._mlp = false;
        clone.classList.add('mlp-rc--gap');
        slot.appendChild(clone);
        this.prepareCards(this.cart);
      }
      pop.hidden = false;
      publish('popup_show', { screen: r.screen });
    }
    async popYes() {
      const btn = this.querySelector('[data-mlp-pop-slot] [data-mlp-card-add]');
      const screen = this._pop && this._pop.screen;
      if (btn && btn.dataset.vid && !btn.disabled) {
        publish('popup_add', { screen, variant: Number(btn.dataset.vid) });
        await this.addVariant(Number(btn.dataset.vid));
      }
      return this.showPopup(screen); // écran suivant : Emballage s'il y en a un, sinon paiement
    }
    popGo() {
      const pop = this.querySelector('[data-mlp-pop]');
      if (pop && this._pop && this._pop.screen === 'pack') {
        const u = {};
        pop.querySelectorAll('[data-mlp-option]:checked').forEach((i) => { u[i.dataset.mlpOption] = 1; });
        this._packUpdates = Object.keys(u).length ? u : null;
      }
      if (pop) pop.hidden = true;
      return this.checkout(this._wantProtection, true, true);
    }
    busy(on) {
      this.querySelectorAll('[data-mlp-go]').forEach((b) => { b.disabled = on; });
      this.classList.toggle('is-busy', on);
    }

    // ---------- Tous les ajouts, d'où qu'ils viennent ----------
    hookNetwork() {
      const self = this;
      const isOwn = (init) => {
        const h = init && init.headers;
        if (!h) return false;
        return typeof h.get === 'function' ? !!h.get(OWN) : !!h[OWN];
      };
      const nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const watched = CART_RE.test(url) && !isOwn(init) && !(input && input.headers && isOwn({ headers: input.headers }));
        const adding = watched && ADD_RE.test(url);
        if (adding) self.ghost();
        const promise = nativeFetch.apply(this, arguments);
        if (watched) promise.then((res) => { if (res.ok) self.refresh(); else if (adding) self.refresh(); }).catch(() => {});
        return promise;
      };
      const open = XMLHttpRequest.prototype.open;
      const send = XMLHttpRequest.prototype.send;
      const setHeader = XMLHttpRequest.prototype.setRequestHeader;
      XMLHttpRequest.prototype.open = function (method, url) { this._mlpUrl = String(url || ''); return open.apply(this, arguments); };
      XMLHttpRequest.prototype.setRequestHeader = function (k) { if (k === OWN) this._mlpOwn = true; return setHeader.apply(this, arguments); };
      XMLHttpRequest.prototype.send = function () {
        if (this._mlpUrl && CART_RE.test(this._mlpUrl) && !this._mlpOwn) {
          if (ADD_RE.test(this._mlpUrl)) self.ghost();
          this.addEventListener('loadend', () => self.refresh());
        }
        return send.apply(this, arguments);
      };
    }
    hookTheme() {
      // Le tiroir du thème (Impact) ne s'ouvre plus : on bloque son ouverture dans l'évènement lui-même.
      document.addEventListener('variant:add', (e) => { if (e.detail) e.detail.blockCartDrawerOpening = true; }, true);
      ['cart:change', 'cart:refresh'].forEach((name) => document.addEventListener(name, (e) => {
        if (!(e.detail && e.detail.mlp)) this.refresh();
      }));
      // Icône panier et liens /cart : notre tiroir (sauf sur la page panier elle-même).
      document.addEventListener('click', (e) => {
        if (this.page) return;
        const a = e.target.closest('[aria-controls="cart-drawer"], a[href$="/cart"], a[href*="/cart?"]');
        if (!a || this.contains(a)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.open();
        this.refresh();
      }, true);
    }
  }

  function sessionFlag(key, set) {
    try {
      if (set) sessionStorage.setItem(key, '1');
      return sessionStorage.getItem(key) === '1';
    } catch (e) { return false; }
  }

  window.customElements.define('mlp-cart', MLPCart);
})();
